/**
 * The self-healing shell guard — AS-11272 M2 (cohort shell routing).
 *
 * my.assisty.ai serves two apps, and Kong picks one per shop from the Redis
 * key `shell:{shop}`. When the edge gets it wrong (an old `assisty_shell`
 * cookie, a key not projected yet) the app that answered checks the store's
 * `frontend_app` from `/stores/detail` and, if the store belongs to the other
 * app, hops there: same path, query and hash, `assisty_shop=` set,
 * `location.replace`. Never a plain reload — without the parameter Kong follows
 * the cookie again (design doc §4). At most one hop per 15 s per tab, so two shells that disagree log
 * `shell_mismatch` instead of ping-ponging the merchant.
 *
 * Fail open throughout: a missing or unknown value renders and logs
 * `shell_missing`. The same rule, same names, lives in atlas
 * (`frontend/src/lib/shellGuard.ts`) — keep the two in step.
 */

import { shopifyAdminHost } from './shopifyBridge';

export type FrontendApp = 'warbler' | 'atlas';

/** sessionStorage: per tab, survives the reload the hop performs. */
export const SHELL_HOP_KEY = 'shell_hop_at';
// No numeric separator: warbler's webpack 4 parser rejects `15_000` even though tsc accepts it.
export const SHELL_HOP_COOLDOWN_MS = 15000;

export type ShellHopDecision =
  | { kind: 'hop'; url: string }
  | { kind: 'stay'; reason: 'match' | 'missing' | 'unknown' | 'recent_hop' };

const APPS: readonly string[] = ['warbler', 'atlas'];

/** The query parameter Kong's shell-router reads first (design doc §5 rule 2). */
export const HOP_SHOP_PARAM = 'assisty_shop';

/**
 * The current document's path + query + hash for the hop; origin dropped.
 *
 * The shop goes in `assisty_shop`, not `shop` (AS-11379): App Bridge's CDN
 * script sends a TOP-LEVEL document that carries `shop=` to the Shopify admin
 * before the app runs, so a hop in a plain tab would leave for
 * `admin.shopify.com`. For the same reason a top-level hop drops any `shop=`
 * it was handed. Framed by the Shopify admin the document is embedded and App
 * Bridge needs `shop` and `host`. Boot has already scrubbed both from the
 * address bar by the time the guard runs, so a framed hop puts `shop` back
 * and restores `host` from App Bridge's own config (`adminHost`) when the URL
 * no longer carries it. `host` is never removed.
 *
 * The path is forced to a single leading slash: `https://host//evil.com/x`
 * parses with pathname `//evil.com/x`, and `location.replace("//evil.com/x")`
 * is protocol-relative — it would leave the origin. The hop must never be a
 * way off the site, whatever URL the merchant was handed.
 */
export function hopUrl(href: string, shopName: string, topLevel: boolean, adminHost: string | null = null): string {
  const url = new URL(href);
  url.searchParams.set(HOP_SHOP_PARAM, shopName);
  if (topLevel) {
    url.searchParams.delete('shop');
  } else {
    url.searchParams.set('shop', shopName);
    if (adminHost && !url.searchParams.has('host')) url.searchParams.set('host', adminHost);
  }
  const path = `/${url.pathname.replace(/^[/\\]+/, '')}`;
  return `${path}?${url.searchParams.toString()}${url.hash}`;
}

export function decideShellHop({
  thisApp,
  frontendApp,
  shopName,
  href,
  topLevel,
  adminHost = null,
  lastHopAt,
  now,
}: {
  thisApp: FrontendApp;
  frontendApp: FrontendApp | string | null | undefined;
  shopName: string;
  href: string;
  /** Not framed by anyone (a plain tab): see `hopUrl`. */
  topLevel: boolean;
  /** App Bridge's `host` when framed by the admin: see `hopUrl`. */
  adminHost?: string | null;
  lastHopAt: number | null;
  now: number;
}): ShellHopDecision {
  if (!frontendApp || !shopName) return { kind: 'stay', reason: 'missing' };
  if (!APPS.includes(frontendApp)) return { kind: 'stay', reason: 'unknown' };
  if (frontendApp === thisApp) return { kind: 'stay', reason: 'match' };
  if (lastHopAt !== null && now - lastHopAt < SHELL_HOP_COOLDOWN_MS) {
    return { kind: 'stay', reason: 'recent_hop' };
  }
  return { kind: 'hop', url: hopUrl(href, shopName, topLevel, adminHost) };
}

export interface ShellGuardDeps {
  href: () => string;
  isTopLevel: () => boolean;
  /** App Bridge's `host`, or null outside the admin. Optional for test doubles. */
  adminHost?: () => string | null;
  now: () => number;
  readHopAt: () => number | null;
  writeHopAt: (t: number) => void;
  replace: (url: string) => void;
  log: (event: 'shell_mismatch' | 'shell_missing', fields: { [key: string]: unknown }) => void;
}

function safeHost(deps: ShellGuardDeps): string | null {
  try {
    return (deps.adminHost && deps.adminHost()) || null;
  } catch {
    return null;
  }
}

function safeRead(read: () => number | null): number | null {
  try {
    const v = read();
    return Number.isFinite(v) ? v : null;
  } catch {
    return null;
  }
}

export function browserShellGuardDeps(): ShellGuardDeps {
  return {
    href: () => window.location.href,
    // A framing question on purpose: it is App Bridge's own test for whether
    // `shop=` sends the document to the admin, not a guess about the host.
    isTopLevel: () => window.top === window.self,
    adminHost: shopifyAdminHost,
    now: () => Date.now(),
    readHopAt: () => {
      const raw = window.sessionStorage.getItem(SHELL_HOP_KEY);
      return raw === null ? null : Number(raw);
    },
    writeHopAt: (t) => window.sessionStorage.setItem(SHELL_HOP_KEY, String(t)),
    replace: (url) => window.location.replace(url),
    // Prod-surfacing on purpose: a mismatch means the edge and the data
    // disagree about a merchant, which someone has to go and look at.
    log: (event, fields) => console.warn(`[shell-guard] ${event}`, fields),
  };
}

/** Decide, and act on a hop. Returns the decision so callers can test it. */
export function runShellGuard(
  { thisApp, frontendApp, shopName }: {
    thisApp: FrontendApp;
    frontendApp: FrontendApp | string | null | undefined;
    shopName: string;
  },
  deps: ShellGuardDeps = browserShellGuardDeps(),
): ShellHopDecision {
  const now = deps.now();
  const decision = decideShellHop({
    thisApp,
    frontendApp,
    shopName,
    href: deps.href(),
    topLevel: deps.isTopLevel(),
    adminHost: safeHost(deps),
    lastHopAt: safeRead(deps.readHopAt),
    now,
  });
  const fields = { shop: shopName, this_app: thisApp, frontend_app: frontendApp ?? null };
  if (decision.kind === 'hop') {
    try {
      deps.writeHopAt(now);
    } catch {
      /* blocked storage: the cooldown is lost, the hop still happens */
    }
    deps.replace(decision.url);
  } else if (decision.reason === 'recent_hop') {
    deps.log('shell_mismatch', fields);
  } else if (decision.reason === 'missing' || decision.reason === 'unknown') {
    deps.log('shell_missing', fields);
  }
  return decision;
}

/**
 * Hop now, ignoring the cooldown: for a move the merchant just asked for (the
 * M6 opt-in). The cooldown exists to stop two disagreeing shells from
 * ping-ponging on boot; it must not swallow an explicit switch made within
 * 15 s of arriving through a hop. Still stamps the hop, so the shell on the
 * other side does not bounce straight back.
 */
export function forceHop(shopName: string, deps: ShellGuardDeps = browserShellGuardDeps()): void {
  try {
    deps.writeHopAt(deps.now());
  } catch {
    /* blocked storage: the hop still happens */
  }
  deps.replace(hopUrl(deps.href(), shopName, deps.isTopLevel(), safeHost(deps)));
}
